import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { fetchTimingService, type FetchResource } from './fetchTimingService';

// CRA exposes only variables prefixed with REACT_APP_, but we also fall back to
// EXPO_PUBLIC_* so the same env file can be shared with Expo/mobile builds.
const rawSupabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ?? process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY: string =
  process.env.REACT_APP_SUPABASE_ANON_KEY ?? process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '';

const normalizeSupabaseUrl = (value: string): string => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withProtocol);
    return url.origin;
  } catch {
    return '';
  }
};

const SUPABASE_URL: string = normalizeSupabaseUrl(rawSupabaseUrl);
// Never use the service-role key in a browser bundle. Use protected Edge Functions instead.
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // eslint-disable-next-line no-console
  console.warn(
    '⚠️  Supabase environment variables are missing. Check your .env file (REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY)'
  );
}

const getProjectRefFromUrl = () => {
  try {
    if (!SUPABASE_URL) return null;
    const host = new URL(SUPABASE_URL).hostname;
    if (!host.endsWith('.supabase.co')) return null;
    const ref = host.split('.')[0];
    return ref || null;
  } catch {
    return null;
  }
};

export const supabaseStorageKey = (() => {
  const ref = getProjectRefFromUrl();
  return ref ? `sb-${ref}-auth-token` : null;
})();

// Web-friendly storage adapter: localStorage (falls back to in-memory if unavailable)
const storageAdapter = {
  getItem: (key: string) => Promise.resolve(localStorage.getItem(key)),
  setItem: (key: string, value: string) => {
    localStorage.setItem(key, value);
    return Promise.resolve();
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key);
    return Promise.resolve();
  },
};

const LOG_AUTH_TOKEN_RESPONSE =
  process.env.REACT_APP_LOG_AUTH_TOKEN_RESPONSE === 'true' ||
  process.env.REACT_APP_LOG_REFRESH_TOKENS === 'true' ||
  process.env.EXPO_PUBLIC_LOG_AUTH_TOKEN_RESPONSE === 'true' ||
  process.env.EXPO_PUBLIC_LOG_REFRESH_TOKENS === 'true' ||
  process.env.EXPO_PUBLIC_DEBUG_TOKENS === 'true';

const isAuthTokenEndpoint = (url: string) => /\/auth\/v1\/token\b/i.test(url);

/** Map PostgREST table name to screen resource for fetch timing. */
function tableToFetchResource(url: string): FetchResource | null {
  try {
    const path = new URL(url, 'https://x').pathname;
    const match = path.match(/\/rest\/v1\/([^/?]+)/i);
    const table = match?.[1]?.toLowerCase();
    if (!table) return null;
    if (['wall_posts', 'wall_comments', 'post_likes'].includes(table)) return 'posts';
    if (['sermons', 'sermon_series', 'sermon_documents'].includes(table)) return 'sermons';
    if (['devotionals', 'devotional_day_progress', 'devotional_series'].includes(table)) return 'devotionals';
    if (['groups', 'group_media', 'group_members', 'group_goals'].includes(table)) return 'groups';
    if (['kids_church_children', 'kids_church_staff', 'kids_church_checkins'].includes(table)) return 'kids_church';
    return null;
  } catch {
    return null;
  }
}

const debugFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (!LOG_AUTH_TOKEN_RESPONSE) return response;
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : (input as Request)?.url;
  if (url && isAuthTokenEndpoint(url)) {
    try {
      const raw = await response.clone().text();
      // eslint-disable-next-line no-console
      console.log('[auth-debug] /token raw response:', raw);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[auth-debug] failed to read /token response body', err);
    }
  }
  return response;
};

const timedFetch: typeof fetch = async (input, init) => {
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : (input as Request)?.url ?? '';
  const resource = url ? tableToFetchResource(url) : null;

  const start = performance.now();
  try {
    const response = await debugFetch(input, init);
    const ms = Math.round(performance.now() - start);
    fetchTimingService.recordFetchMs(ms, resource ?? undefined);
    return response;
  } catch (err) {
    const ms = Math.round(performance.now() - start);
    fetchTimingService.recordFetchMs(ms, resource ?? undefined);
    throw err;
  }
};

export const supabase: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    storage: storageAdapter,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      'X-Client-Info': 'church-web-dashboard',
    },
    fetch: timedFetch,
  },
});

// Re-export common types for convenience
export * from '@supabase/supabase-js';
